/** 判定「得分并列」的误差范围，得分是同分母的比值，这里只为规避浮点误差 */
const SAME_SCORE_EPSILON = 1e-6;

/**
 * @description 归一化名称：转小写并去掉所有非字母、非数字字符（空格、-、_、&、() 等），
 * @description 因此「Shopee-仓库 1」与「shopee仓库1」等价；中文、泰文、日文等文字保留
 */
const normalize = (name: string) => name.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');

/** 求两个字符串的最长公共子串长度 */
const longestCommonSubstring = (a: string, b: string) => {
  let best = 0;
  let prev = new Array<number>(b.length + 1).fill(0);
  for (let i = 1; i <= a.length; i++) {
    const current = new Array<number>(b.length + 1).fill(0);
    for (let j = 1; j <= b.length; j++) {
      if (a[i - 1] === b[j - 1]) {
        current[j] = prev[j - 1] + 1;
        if (current[j] > best) {
          best = current[j];
        }
      }
    }
    prev = current;
  }
  return best;
};

/**
 * @description 相似度 = 候选项名称包含了目标名称的多少，1 表示完整包含
 * @description 如目标为「仓库1」时，「Shopee-仓库1-顺丰」得 1，「Shopee-仓库2-J&T」得 2/3
 */
const similarity = (target: string, candidate: string) => {
  const normalizedTarget = normalize(target);
  const normalizedCandidate = normalize(candidate);
  if (!normalizedTarget || !normalizedCandidate) {
    return 0;
  }
  if (normalizedCandidate.includes(normalizedTarget)) {
    return 1;
  }
  return longestCommonSubstring(normalizedTarget, normalizedCandidate) / normalizedTarget.length;
};

/** 取出相似度最高的一组候选项，可能并列；全都不相似时整组返回，由调用方强行选择 */
const highestScored = <T>(candidates: T[], score: (candidate: T) => number) => {
  let best = 0;
  let group: T[] = [];
  candidates.forEach((candidate) => {
    const current = score(candidate);
    if (!group.length || current > best + SAME_SCORE_EPSILON) {
      best = current;
      group = [candidate];
    } else if (Math.abs(current - best) <= SAME_SCORE_EPSILON) {
      group.push(candidate);
    }
  });
  return group;
};

/**
 * @description 在一组候选项中，按名称找出与目标最相似的一项，用于「最相似匹配」后的自动选择
 * @description 先用目标名称匹配，最高分唯一时直接采用
 * @description 最高分并列时，再用「平台-目标名称」在这些并列的候选里挑最相似的一项，
 * @description 如平台为 Shopee、目标名称为「仓库1」时，用「Shopee-仓库1」区分 Shopee-仓库1-顺丰 与 Lazada-仓库1-极兔
 * @description 没有相似度大于 0 的候选项时，按候选顺序强行选一个，仅候选项为空时返回 undefined
 * @param target 目标名称，如所选仓库的名称
 * @param candidates 候选项列表
 * @param getName 从候选项中取出参与匹配的名称
 * @param platform 平台名称，如 Shopee，仅在最高分并列时参与匹配
 */
function findMostSimilar<T>(
  target: string | undefined,
  candidates: T[] | undefined,
  getName: (candidate: T) => string,
  platform?: string,
): T | undefined {
  const keyword = target?.trim();
  if (!keyword || !candidates?.length) {
    return undefined;
  }
  const tied = highestScored(candidates, (candidate) => similarity(keyword, getName(candidate)));
  if (tied.length === 1) {
    return tied[0];
  }
  const trimmed = platform?.trim();
  if (!trimmed) {
    return tied[0];
  }
  const withPlatform = `${trimmed}-${keyword}`;
  return highestScored(tied, (candidate) => similarity(withPlatform, getName(candidate)))[0];
}

export default findMostSimilar;
